import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../components/common/Button';
import { agentApi } from '../../../services/agentApi';
import { AgentSettingsUpdate, AgentSettingsView, AgentStatus } from '../../../types/interfaces';
import { formatDateTime } from '../../../utils/dates';
import './agent.css';

/** Same text as the server's 422 for a base URL host change without a key. */
const KEY_AGAIN = "Changing the base URL's host needs the API key again";

type HeaderRow = { name: string; value: string };

/**
 * Error text for display. api.js puts the response `detail` in `message`: a string for the
 * server's own 422s, a list of `{msg}` objects for FastAPI validation errors.
 */
export function errorDetail(err: any, fallback: string): string {
  const message = err?.message;
  if (typeof message === 'string' && message) return message;
  if (Array.isArray(message)) {
    const parts = message.map((d: any) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  return fallback;
}

/** scheme://host:port with default ports filled in and the host lower-cased; null if unparsable. */
export function urlOrigin(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const u = new URL(value.trim());
    const port = u.port || (u.protocol === 'https:' ? '443' : u.protocol === 'http:' ? '80' : '');
    return `${u.protocol}//${u.hostname.toLowerCase()}:${port}`;
  } catch {
    return null;
  }
}

/** LLM connection settings, the connection test and the knowledge refresh (/admin?section=agent&tab=settings). */
const AgentSettings: React.FC = () => {
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [stored, setStored] = useState<AgentSettingsView | null>(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [model, setModel] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [apiKey, setApiKey] = useState('');
  const [mask, setMask] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [refreshNote, setRefreshNote] = useState('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const fill = (view: AgentSettingsView) => {
    setStored(view);
    setBaseUrl(view.base_url || '');
    setModel(view.model || '');
    setApiVersion(view.api_version || '');
    setHeaders(Object.entries(view.extra_headers || {}).map(([name, value]) => ({ name, value })));
    setApiKey('');
    setMask(view.mask_personal_data);
  };

  const loadStatus = useCallback(async () => {
    const s = await agentApi.status();
    if (mounted.current) setStatus(s);
    return s;
  }, []);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const s = await loadStatus();
      if (!s.enabled) return;
      const view = await agentApi.getSettings();
      if (mounted.current) fill(view);
    } catch (err) {
      if (mounted.current) setLoadError(errorDetail(err, 'Could not load the agent settings'));
    }
  }, [loadStatus]);

  useEffect(() => { load(); }, [load]);

  const hostChanged = !!stored?.base_url && urlOrigin(baseUrl) !== urlOrigin(stored.base_url);
  const headerMap = (rows: HeaderRow[]) =>
    JSON.stringify(Object.fromEntries(rows.filter((h) => h.name.trim()).map((h) => [h.name.trim(), h.value])));
  /** Unsaved edits: the connection test uses the saved settings, so it waits for a save. */
  const dirty = !!stored && (
    baseUrl.trim() !== (stored.base_url || '')
    || model.trim() !== (stored.model || '')
    || apiVersion.trim() !== (stored.api_version || '')
    || headerMap(headers) !== JSON.stringify(stored.extra_headers || {})
    || mask !== stored.mask_personal_data
    || apiKey.trim() !== '');

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError('');
    setSaved(false);
    if (hostChanged && stored?.api_key_set && !apiKey.trim()) {
      setSaveError(`${KEY_AGAIN}. Enter the API key again.`);
      return;
    }
    const body: AgentSettingsUpdate = {
      base_url: baseUrl.trim(),
      model: model.trim(),
      api_version: apiVersion.trim() || null,
      extra_headers: Object.fromEntries(
        headers.filter((h) => h.name.trim()).map((h) => [h.name.trim(), h.value])),
      mask_personal_data: mask,
    };
    if (apiKey.trim()) body.api_key = apiKey.trim(); // blank keeps the stored key
    setSaving(true);
    try {
      const view = await agentApi.saveSettings(body);
      if (!mounted.current) return;
      fill(view);
      setSaved(true);
    } catch (err) {
      if (mounted.current) setSaveError(errorDetail(err, 'Could not save the settings'));
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await agentApi.testSettings();
      if (mounted.current) setTestResult(result);
    } catch (err) {
      if (mounted.current) setTestResult({ ok: false, detail: errorDetail(err, 'The connection test failed') });
    } finally {
      if (mounted.current) setTesting(false);
    }
  };

  const refreshKnowledge = async () => {
    setRefreshNote('');
    try {
      const { job_id } = await agentApi.refreshKnowledge();
      if (mounted.current) setRefreshNote(`Knowledge refresh queued (job ${job_id}). Build times update when it finishes.`);
    } catch (err) {
      if (mounted.current) setRefreshNote(errorDetail(err, 'Could not queue the refresh'));
    }
  };

  const setHeader = (i: number, change: Partial<HeaderRow>) =>
    setHeaders((rows) => rows.map((row, j) => (j === i ? { ...row, ...change } : row)));

  if (loadError) {
    return (
      <div className="agent-banner agent-banner--error" role="alert">
        {loadError}{' '}
        <Button variant="outline" size="sm" onClick={() => { load(); }}>Retry</Button>
      </div>
    );
  }
  if (!status) return <p className="agent-meta">Loading…</p>;
  if (!status.enabled) {
    return (
      <div className="agent-banner agent-banner--warning" role="status">
        The agent is disabled: {status.reason}
      </div>
    );
  }
  if (!stored) return <p className="agent-meta">Loading…</p>;

  return (
    <div className="agent-panel">
      <form className="agent-form" onSubmit={save} noValidate>
        <h3 className="agent-panel__title">LLM connection</h3>
        <label className="agent-field">
          <span>Base URL</span>
          <input className="input input--default input--sm" value={baseUrl} maxLength={2000}
            onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://llm.example.edu/v1" />
        </label>
        <p className="agent-meta">
          Use https. Plain http is allowed only for localhost or 127.0.0.1. Link-local addresses are refused.
        </p>
        {hostChanged && stored.api_key_set && (
          <p className="agent-meta agent-meta--warning">{KEY_AGAIN}; stored secret headers are dropped unless you enter them again.</p>
        )}
        <label className="agent-field">
          <span>Model</span>
          <input className="input input--default input--sm" value={model} maxLength={200}
            onChange={(e) => setModel(e.target.value)} />
        </label>
        <label className="agent-field">
          <span>API version</span>
          <input className="input input--default input--sm" value={apiVersion} maxLength={200}
            onChange={(e) => setApiVersion(e.target.value)} placeholder="Only for Azure-style endpoints" />
        </label>
        <label className="agent-field">
          <span>API key</span>
          <input className="input input--default input--sm" type="password" autoComplete="new-password" value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={stored.api_key_set ? `Saved key ${stored.api_key_hint || '…'}; leave blank to keep it` : 'Not set'} />
        </label>

        <fieldset className="agent-fieldset">
          <legend>Extra headers</legend>
          {headers.length === 0 && <p className="agent-meta">None.</p>}
          {headers.map((h, i) => (
            <div className="agent-header-row" key={i}>
              <input className="input input--default input--sm" aria-label={`Header ${i + 1} name`} value={h.name}
                maxLength={200} onChange={(e) => setHeader(i, { name: e.target.value })} placeholder="Name" />
              <input className="input input--default input--sm" aria-label={`Header ${i + 1} value`} value={h.value}
                maxLength={2000} onChange={(e) => setHeader(i, { value: e.target.value })} placeholder="Value" />
              <button type="button" className="agent-link-button"
                onClick={() => setHeaders((rows) => rows.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
          {headers.length < 20 && (
            <button type="button" className="agent-link-button"
              onClick={() => setHeaders((rows) => rows.concat({ name: '', value: '' }))}>Add header</button>
          )}
          <p className="agent-meta">Secret values show as ••••; leave them as they are to keep the stored value.</p>
        </fieldset>

        <label className="agent-check">
          <input type="checkbox" checked={mask} onChange={(e) => setMask(e.target.checked)} />
          <span>Mask personal data (emails and names) before it is sent to the LLM</span>
        </label>

        {saveError && <div className="agent-inline-error" role="alert">{saveError}</div>}
        {saved && !dirty && <div className="agent-meta" role="status">Saved.</div>}
        <div className="agent-plan__buttons">
          <Button variant="primary" size="sm" type="submit" disabled={saving}>Save</Button>
          <Button variant="outline" size="sm" onClick={test} disabled={testing || dirty}
            title={dirty ? 'Save first: the test uses the saved settings' : undefined}>Test connection</Button>
          {dirty && <span className="agent-meta">Unsaved changes: save before testing (the test uses the saved settings).</span>}
          {testResult && (
            <span className={testResult.ok ? 'agent-meta' : 'agent-inline-error'} role="status">
              {testResult.ok ? `Connection OK (${testResult.detail})` : `Connection failed: ${testResult.detail}`}
            </span>
          )}
        </div>
      </form>

      <section className="agent-form" aria-label="Knowledge">
        <h3 className="agent-panel__title">Knowledge</h3>
        <p className="agent-meta">The agent's index of the API, schema and code. It is rebuilt when stale; refresh after a deploy.</p>
        <ul className="agent-plain-list">
          {status.knowledge.length === 0 && <li className="agent-meta">Not built yet.</li>}
          {status.knowledge.map((k) => (
            <li key={k.source}>
              <b>{k.source}</b> <span className="agent-meta">built {formatDateTime(k.built_at, 'never')}</span>
            </li>
          ))}
        </ul>
        <div className="agent-plan__buttons">
          <Button variant="outline" size="sm" onClick={refreshKnowledge}>Refresh knowledge</Button>
          <Button variant="ghost" size="sm" onClick={() => { loadStatus().catch(() => undefined); }}>Reload status</Button>
          {refreshNote && <span className="agent-meta" role="status">{refreshNote}</span>}
        </div>
      </section>
    </div>
  );
};

export default AgentSettings;
